/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { HTMLAttributes } from 'preact/compat';
import { classes, Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { CartModel } from '@/cart/data/models/cart-model';
import { CartSummaryTable as CartSummaryTableComponent } from '@/cart/components';
import {
  Price,
  Skeleton,
  SkeletonRow,
  Image,
  ImageProps,
  Icon,
  Button,
} from '@adobe-commerce/elsie/components';
import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import { events } from '@adobe-commerce/event-bus';
import {
  Cart,
  OrderError,
  Trash,
  WarningWithCircle,
} from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Item } from '@/cart/components/CartSummaryTable/Elements/Item/Item';
import { useCartItems } from '@/cart/hooks/useCartItems';
import { getPersistedCartData } from '@/cart/lib/persisted-data';
import { InLineAlert } from '@adobe-commerce/elsie/components/InLineAlert';
import { addProductsToCart } from '@/cart/api/addProductsToCart/addProductsToCart';
import { VNode } from 'preact';
import { EmptyCart } from '@/cart/containers/EmptyCart';

export interface CartSummaryTableContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  /** Initial data for the cart */
  initialData?: CartModel | null;
  /** Optional CSS class name for custom styling */
  className?: string;
  /** Slots for customizing different parts of the table */
  slots?: {
    /** Slot for customizing the item cell content */
    Item?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the price cell content */
    Price?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the quantity cell content */
    Quantity?: SlotProps<{
      item: CartModel['items'][number];
      isUpdating: boolean;
      quantityInputValue: number;
      handleInputChange: (e: Event) => void;
      itemUpdateErrors: Map<string, string>;
    }>;
    /** Slot for customizing the subtotal cell content */
    Subtotal?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the thumbnail image on an item */
    Thumbnail?: SlotProps<{
      item: CartModel['items'][number];
      defaultImageProps: ImageProps;
      index: number;
    }>;
    /** Slot for customizing the product title on an item */
    ProductTitle?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the product sku on an item */
    Sku?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the product configurations on an item */
    Configurations?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the product alert on an item */
    ItemAlert?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the product warning on an item */
    ItemWarning?: SlotProps<{ item: CartModel['items'][number] }>;
    /** Slot for customizing the actions on an item */
    Actions?: SlotProps<{
      item: CartModel['items'][number];
      itemsUpdating: Map<string, { isUpdating: boolean; updatedValue: number }>;
      setItemUpdating: (uid: string, state: boolean) => void;
      setItemUpdateError: (uid: string, error: string) => void;
    }>;
    UndoBanner?: SlotProps<{
      item: CartModel['items'][number];
      loading: boolean;
      error?: string;
      onUndo: () => void;
      onDismiss: () => void;
    }>;
    /** Slot for customizing the empty cart */
    EmptyCart?: SlotProps;
  };
  /** Function for getting the product page route */
  routeProduct?: (item: CartModel['items'][number]) => string;
  /** Function for getting the empty cart CTA route */
  routeEmptyCartCTA?: () => string;
  /** Whether to allow quantity updates */
  allowQuantityUpdates?: boolean;
  /** Whether to allow remove items */
  allowRemoveItems?: boolean;
  /** On quantity update */
  onQuantityUpdate?: (
    item: CartModel['items'][number],
    quantity: number
  ) => void;
  /** On item remove */
  onItemRemove?: (item: CartModel['items'][number]) => void;
  /** Whether to enable undo functionality for removed items */
  undo?: boolean;
}

/**
 * Skeleton loader component for the cart summary table
 */
const CartSummaryTableSkeleton = () => {
  return (
    <Skeleton
      data-testid="cart-summary-table-skeleton"
      className="cart-cart-summary-table__skeleton"
      rowGap="medium"
    >
      <SkeletonRow
        variant="row"
        size="xlarge"
        fullWidth={true}
        lines={4}
        multilineGap="small"
      />
    </Skeleton>
  );
};

/**
 * Container component for CartSummaryTable that provides slots for customizing the table cells
 * and handles data management
 */
export const CartSummaryTable: Container<
  CartSummaryTableContainerProps,
  CartModel | null
> = ({
  initialData = null,
  routeProduct,
  routeEmptyCartCTA,
  slots,
  className,
  allowQuantityUpdates = true,
  allowRemoveItems = true,
  onQuantityUpdate,
  onItemRemove,
  undo = false,
  ...props
}) => {
  const [loading, setLoading] = useState(!initialData);
  const [cartModelData, setCartModelData] = useState<CartModel | null>(
    initialData
  );
  const [recentlyRemovedItems, setRecentlyRemovedItems] = useState<
    {
      item: CartModel['items'][number];
      index: number;
      loading: boolean;
      error?: string;
    }[]
  >([]);
  /** I18n text for the cart summary table */
  const dictionary = useText({
    file: 'Cart.CartItem.file',
    files: 'Cart.CartItem.files',
    heading: 'Cart.Cart.heading',
    message: 'Cart.CartItem.message',
    recipient: 'Cart.CartItem.recipient',
    regularPrice: 'Cart.CartItem.regularPrice',
    discountedPrice: 'Cart.CartItem.discountedPrice',
    sender: 'Cart.CartItem.sender',
    lowInventory: 'Cart.CartItem.lowInventory',
    insufficientQuantity: 'Cart.CartItem.insufficientQuantity',
    insufficientQuantityGeneral: 'Cart.CartItem.insufficientQuantityGeneral',
    outOfStockHeading: 'Cart.OutOfStockMessage.heading',
    outOfStockDescription: 'Cart.OutOfStockMessage.message',
    outOfStockAlert: 'Cart.OutOfStockMessage.alert',
    removeAction: 'Cart.OutOfStockMessage.action',
    notAvailableMessage: 'Cart.CartItem.notAvailableMessage',
    viewMore: 'Cart.Cart.viewMore',
    viewAll: 'Cart.Cart.viewAll',
    discountPercent: 'Cart.CartItem.discountPercentage',
    savingsAmount: 'Cart.CartItem.savingsAmount',
    includingTax: 'Cart.CartItem.includingTax',
    excludingTax: 'Cart.CartItem.excludingTax',
    remove: 'Dropin.CartItem.remove.label',
    removeDefault: 'Dropin.CartItem.removeDefault.label',
    quantity: 'Dropin.CartItem.quantity.label',
    itemBeingRemoved: 'Cart.CartItem.itemBeingRemoved',
    itemRemoved: 'Cart.CartItem.itemRemoved',
    itemRemovedDescription: 'Cart.CartItem.itemRemovedDescription',
    undoAction: 'Cart.CartItem.undoAction',
    dismissAction: 'Cart.CartItem.dismissAction',
  });

  /** Helper functions */
  const {
    showIncludedTaxPrice,
    showExcludingTaxPrice,
    itemsUpdating,
    itemUpdateErrors,
    getConfiguration,
    getPriceProps,
    getSubtotalProps,
    debouncedQuantityChange,
    setItemsUpdating,
    getWarningMessage,
    handleRemoveItem,
    setItemUpdating,
    setItemUpdateError,
  } = useCartItems({ dictionary, onQuantityUpdate, onItemRemove });

  // Helper to add to recently removed items for undo
  function addToRecentlyRemovedItems(uid: string) {
    const cart = getPersistedCartData();
    const item = cart?.items.find((i) => i.uid === uid);
    if (item) {
      setRecentlyRemovedItems((prev) => {
        const index = cart.items.findIndex((i) => i.uid === uid);
        if (index !== -1 && !prev.some((r) => r.item.uid === uid)) {
          // Emit the event for analytics or listeners
          events.emit('cart/product/removed', { item, index });
          return [...prev, { item, index, loading: false, error: undefined }];
        }
        return prev;
      });
    }
  }

  const handleUndo = createUndoHandler(
    recentlyRemovedItems,
    setRecentlyRemovedItems
  );

  const handleDismiss = createDismissHandler(setRecentlyRemovedItems);

  const handleRemoveWithUndo = useCallback(
    (item: CartModel['items'][number]) => {
      if (undo) {
        addToRecentlyRemovedItems(item.uid);
      }
      handleRemoveItem(item); // original remove logic
    },
    [undo, handleRemoveItem]
  );

  /**
   * Handle the cart data event
   */
  useEffect(() => {
    const cartDataEvent = events.on(
      'cart/data',
      (payload: Cart | null) => {
        setLoading(true);
        setCartModelData(payload as CartModel);
        setItemsUpdating(new Map());
        setLoading(false);
      },
      { eager: true }
    );

    return () => {
      cartDataEvent?.off();
    };
  }, [setLoading, setCartModelData, setItemsUpdating]);

  // Transform cart items into table entries with slots for customization
  const entries = useMemo(
    () =>
      cartModelData?.items
        ?.sort((a, b) => {
          // Sort out of stock items and items with update errors last
          // to show them at the top since we are rendering the list in reverse order
          if (itemUpdateErrors.has(a.uid) && !itemUpdateErrors.has(b.uid))
            return -1;
          if (!itemUpdateErrors.has(a.uid) && itemUpdateErrors.has(b.uid))
            return 1;
          if (a.outOfStock && !b.outOfStock) return -1;
          if (!a.outOfStock && b.outOfStock) return 1;
          return 0;
        })
        .map((item, index) => {
          const isUpdating = itemsUpdating.has(item.uid);
          const warningMessage = getWarningMessage(item);

          const productTitle = (
            <Slot
              name="ProductTitle"
              slot={slots?.ProductTitle}
              context={{ item }}
            >
              {routeProduct ? (
                <a href={routeProduct(item)}>{item.name}</a>
              ) : (
                item.name
              )}
            </Slot>
          );

          const defaultImageProps = {
            src: item.image.src,
            alt: item.image.alt,
            width: '300',
            height: '300',
            params: { width: 300 },
          };

          const defaultImage = (
            <Image
              data-testid={`cart-table-item-image-${item.sku}`}
              loading={index < 4 ? 'eager' : 'lazy'}
              {...defaultImageProps}
            />
          );

          const productImage = (
            <Slot
              name="Thumbnail"
              slot={slots?.Thumbnail}
              className="cart-cart-summary-table__item-image-wrapper"
              context={{
                item,
                defaultImageProps: {
                  ...defaultImageProps,
                  loading: index < 4 ? 'eager' : 'lazy',
                },
                index,
              }}
            >
              {routeProduct ? (
                <a href={routeProduct(item)}>{defaultImage}</a>
              ) : (
                defaultImage
              )}
            </Slot>
          );

          const productSku = (
            <Slot name="Sku" slot={slots?.Sku} context={{ item }}>
              {item.sku}
            </Slot>
          );

          const itemAlert =
            item.outOfStock || slots?.ItemAlert ? (
              <Slot name="ItemAlert" slot={slots?.ItemAlert} context={{ item }}>
                {item.outOfStock && (
                  <div
                    className="cart-cart-summary-table__item-quantity-alert-wrapper"
                    data-testid={`cart-table-item-quantity-alert-${item.uid}`}
                  >
                    <Icon
                      className="cart-cart-summary-table__item-quantity-alert-icon"
                      source={OrderError}
                      size={'16'}
                    />
                    <span className="cart-cart-summary-table__item-quantity-alert-text">
                      {dictionary.outOfStockAlert}
                    </span>
                  </div>
                )}
              </Slot>
            ) : undefined;

          const itemWarning =
            warningMessage || slots?.ItemWarning ? (
              <Slot
                name="ItemWarning"
                slot={slots?.ItemWarning}
                context={{ item }}
              >
                {warningMessage && (
                  <div
                    className="cart-cart-summary-table__item-quantity-warning-wrapper"
                    data-testid={`cart-table-item-quantity-warning-${item.uid}`}
                  >
                    <Icon
                      className="cart-cart-summary-table__item-quantity-warning-icon"
                      source={WarningWithCircle}
                      size={'16'}
                    />
                    <span className="cart-cart-summary-table__item-quantity-warning-text">
                      {warningMessage}
                    </span>
                  </div>
                )}
              </Slot>
            ) : undefined;

          const itemConfigurations = getConfiguration({ item });
          const productConfigurations =
            itemConfigurations || slots?.Configurations ? (
              <Slot
                name="Configurations"
                slot={slots?.Configurations}
                context={{ item }}
              >
                {itemConfigurations && (
                  <ul
                    className="cart-cart-summary-table__item-configurations"
                    data-testid={`cart-table-item-configurations-${item.sku}`}
                  >
                    {Object.entries(itemConfigurations).map(([key, value]) => (
                      <li
                        className="cart-cart-summary-table__item-configuration"
                        data-testid={`cart-table-item-configuration-${key}`}
                        key={key}
                      >
                        <span className="cart-cart-summary-table__item-configuration-label">
                          {key}:
                        </span>
                        <span className="cart-cart-summary-table__item-configuration-value">
                          {value}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Slot>
            ) : undefined;

          const { subtotalProps, subtotalDiscountProps } =
            getSubtotalProps(item);

          const updatedValue = itemsUpdating.get(item.uid)?.updatedValue;
          const quantityInputValue =
            isUpdating && !isNaN(updatedValue) ? updatedValue : item.quantity;

          const handleInputChange = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const value = parseInt(target.value, 10);

            debouncedQuantityChange(item, value);
          };

          const isBeingRemoved =
            itemsUpdating.has(item.uid) &&
            itemsUpdating.get(item.uid)?.updatedValue === 0;
          const actions = (
            <Slot
              name="Actions"
              slot={slots?.Actions}
              context={{
                item,
                itemsUpdating,
                setItemUpdating,
                setItemUpdateError,
              }}
            >
              {allowRemoveItems && !isBeingRemoved && (
                <Button
                  variant="tertiary"
                  size="medium"
                  className="cart-cart-summary-table__item-remove-button"
                  data-testid={`cart-table-item-remove-${item.uid}`}
                  icon={<Icon source={Trash} size={'32'} />}
                  onClick={() => handleRemoveWithUndo(item)}
                  aria-label={
                    dictionary.remove?.replace('{product}', item.name) ||
                    dictionary.removeDefault
                  }
                />
              )}
            </Slot>
          );

          return {
            key: item.uid,
            uid: item.uid,
            updating: isUpdating,
            hasError: item.outOfStock || itemUpdateErrors.has(item.uid),
            item: (
              <Slot name="Item" slot={slots?.Item} context={{ item }}>
                <Item
                  productTitle={productTitle}
                  image={productImage}
                  sku={productSku}
                  configurations={productConfigurations}
                  alert={itemAlert}
                  warning={itemWarning}
                />
              </Slot>
            ),
            price: (
              <Slot
                name="Price"
                className="cart-cart-summary-table__item-price"
                slot={slots?.Price}
                context={{ item }}
              >
                <Price {...getPriceProps(item)} />
                {showIncludedTaxPrice && (
                  <span className="cart-cart-summary-table__item-price-tax-label">
                    {dictionary.includingTax}:&nbsp;
                    <Price
                      amount={item.taxedPrice?.value}
                      currency={item.taxedPrice?.currency}
                    />
                  </span>
                )}
                {showExcludingTaxPrice && (
                  <span className="cart-cart-summary-table__item-price-tax-label">
                    {dictionary.excludingTax}:&nbsp;
                    <Price
                      amount={item.regularPrice?.value}
                      currency={item.regularPrice?.currency}
                    />
                  </span>
                )}
              </Slot>
            ),
            quantity: (
              <Slot
                name="Quantity"
                className="cart-cart-summary-table__item-qty"
                slot={slots?.Quantity}
                context={{
                  item,
                  isUpdating,
                  quantityInputValue,
                  handleInputChange,
                  itemUpdateErrors,
                }}
              >
                {allowQuantityUpdates ? (
                  <input
                    id={`cart-table-item-quantity-${item.uid}`}
                    data-testid={`cart-table-item-quantity-${item.uid}`}
                    type="number"
                    min="1"
                    value={quantityInputValue}
                    disabled={isUpdating}
                    aria-label={dictionary.quantity}
                    className={classes([
                      'cart-cart-summary-table__cell-qty-input',
                      'cart-cart-summary-table__cell-qty-updater',
                      [
                        'cart-cart-summary-table__cell-qty-updater--disabled',
                        isUpdating,
                      ],
                      [
                        'cart-cart-summary-table__cell-qty-updater--error',
                        itemUpdateErrors.has(item.uid),
                      ],
                    ])}
                    onChange={handleInputChange}
                  />
                ) : (
                  <span>{item.quantity}</span>
                )}
              </Slot>
            ),
            subtotal: (
              <Slot
                name="Subtotal"
                className="cart-cart-summary-table__item-subtotal"
                slot={slots?.Subtotal}
                context={{ item }}
              >
                <Price {...subtotalProps} />
                {subtotalDiscountProps && <Price {...subtotalDiscountProps} />}
                {showIncludedTaxPrice && (
                  <span className="cart-cart-summary-table__item-subtotal-tax-label">
                    {dictionary.includingTax}:&nbsp;
                    <Price
                      amount={item.rowTotalIncludingTax?.value}
                      currency={item.rowTotalIncludingTax?.currency}
                    />
                  </span>
                )}
                {showExcludingTaxPrice && (
                  <span className="cart-cart-summary-table__item-subtotal-tax-label">
                    {dictionary.excludingTax}:&nbsp;
                    <Price
                      amount={item.rowTotal?.value}
                      currency={item.rowTotal?.currency}
                    />
                  </span>
                )}
              </Slot>
            ),
            actions,
          };
        }) || [],
    [
      cartModelData,
      itemsUpdating,
      allowQuantityUpdates,
      routeProduct,
      slots,
      itemUpdateErrors,
      dictionary,
      getConfiguration,
      allowRemoveItems,
      debouncedQuantityChange,
      getWarningMessage,
      getPriceProps,
      getSubtotalProps,
      setItemUpdating,
      setItemUpdateError,
      showIncludedTaxPrice,
      showExcludingTaxPrice,
      handleRemoveWithUndo,
    ]
  );

  /**
   * Render the cart summary table skeleton if the cart is loading
   */
  if (loading) {
    return <CartSummaryTableSkeleton />;
  }
  const renderTableWithUndoBanners = () => {
    if (!cartModelData?.items) return null;
    const tableEntries: any[] = [];
    const items = cartModelData.items;

    // Process items up to the available entries length
    for (let i = 0; i <= entries.length; i++) {
      // Render undo banners for this index position if undo is enabled
      if (undo) {
        recentlyRemovedItems
          .filter((r) => r.index === i)
          .forEach(({ item, error }) => {
            const isUndoBeingRemoved = getIsUndoBeingRemoved(item.uid, itemsUpdating);
            const undoBanner = createUndoBanner(
              { item, index: i, error },
              item,
              isUndoBeingRemoved,
              dictionary,
              handleUndo,
              handleDismiss,
              slots
            );
            
            // Add undo banner as a special entry
            tableEntries.push({
              uid: `undo-banner-${item.uid}`,
              updating: false,
              hasError: false,
              item: <div />,
              price: <div />,
              quantity: <div />,
              subtotal: <div />,
              actions: <div />,
              undoBanner,
            });
          });
      }
      
      // Render cart item if not at the end and within the entries limit
      if (i < entries.length && i < items.length) {
        tableEntries.push(entries[i]);
      }
    }
    
    return (
      <CartSummaryTableComponent
        entries={tableEntries}
        className={className}
        {...props}
      />
    );
  };

  /**
   * Create empty cart component with undo banner support
   */
  const emptyCart = (
    <Slot name="EmptyCart" slot={slots?.EmptyCart} context={{}}>
      <EmptyCart ctaLinkURL={routeEmptyCartCTA?.()} />
    </Slot>
  );

  /**
   * Choose between original table entries and undo banner version (matches CartSummaryList logic)
   */
  const tableContent = 
    cartModelData?.totalQuantity ||
    (undo && recentlyRemovedItems.length > 0) ? (
      !undo ? (
        <CartSummaryTableComponent
          entries={entries}
          className={className}
          {...props}
        />
      ) : recentlyRemovedItems.length > 0 ? (
        renderTableWithUndoBanners()
      ) : (
        <CartSummaryTableComponent
          entries={entries}
          className={className}
          {...props}
        />
      )
    ) : emptyCart;

  /**
   * Render the cart summary table
   */
  return tableContent;
};

CartSummaryTable.getInitialData = async function () {
  return getPersistedCartData();
};

export const createUndoHandler = (
  recentlyRemovedItems: {
    item: CartModel['items'][number];
    index: number;
    loading: boolean;
    error?: string;
  }[],
  setRecentlyRemovedItems: (
    updater: (
      prev: {
        item: CartModel['items'][number];
        index: number;
        loading: boolean;
        error?: string;
      }[]
    ) => {
      item: CartModel['items'][number];
      index: number;
      loading: boolean;
      error?: string;
    }[]
  ) => void
) => {
  return async (uid: string) => {
    setRecentlyRemovedItems((prev) =>
      prev.map((r) => (r.item.uid === uid ? { ...r, error: undefined } : r))
    );

    const removed = recentlyRemovedItems.find((r) => r.item.uid === uid);
    if (!removed) return;

    try {
      await addProductsToCart([
        {
          sku: removed.item.sku,
          parentSku: removed.item.topLevelSku,
          quantity: removed.item.quantity,
          optionsUIDs:
            removed.item.bundleOptionsUIDs || // Use bundle option UIDs for bundle products
            (removed.item.selectedOptionsUIDs
              ? Object.values(removed.item.selectedOptionsUIDs)
              : undefined),
          enteredOptions: removed.item.customizableOptions
            ? Object.entries(removed.item.customizableOptions).map(
                ([uid, value]) => ({ uid, value })
              )
            : undefined,
        },
      ]);
      setRecentlyRemovedItems((prev) => prev.filter((r) => r.item.uid !== uid));
    } catch (error: any) {
      setRecentlyRemovedItems((prev) =>
        prev.map((r) =>
          r.item.uid === uid
            ? { ...r, error: error?.message || 'Failed to restore item.' }
            : r
        )
      );
    }
  };
};

export const createDismissHandler = (
  setRecentlyRemovedItems: (
    updater: (
      prev: {
        item: CartModel['items'][number];
        index: number;
        loading: boolean;
        error?: string;
      }[]
    ) => {
      item: CartModel['items'][number];
      index: number;
      loading: boolean;
      error?: string;
    }[]
  ) => void
) => {
  return (uid: string) => {
    setRecentlyRemovedItems((prev) => prev.filter((r) => r.item.uid !== uid));
  };
};

/**
 * Helper function to determine if an undo is currently being removed
 * This makes the logic more explicit and testable
 */
export const getIsUndoBeingRemoved = (
  itemUid: string,
  itemsUpdating: Map<string, { isUpdating: boolean; updatedValue: number }>
): boolean => {
  return itemsUpdating.has(itemUid) && itemsUpdating.get(itemUid)?.updatedValue === 0;
};

/**
 * Helper function to create additional actions for undo banner
 * This makes the ternary operator logic more explicit and testable
 */
export const createUndoBannerActions = (
  isUndoBeingRemoved: boolean,
  dictionary: any,
  handleUndo: (uid: string) => void,
  handleDismiss: (uid: string) => void,
  itemUid: string,
  itemName: string
) => {
  if (isUndoBeingRemoved) {
    return []; // Hide buttons while removing
  }
  
  return [
    {
      label: dictionary.undoAction,
      onClick: () => {
        handleUndo(itemUid);
      },
      'aria-label': `${dictionary.undoAction} remove ${itemName}`,
    },
    {
      label: dictionary.dismissAction,
      onClick: () => {
        handleDismiss(itemUid);
      },
      'aria-label': `${dictionary.dismissAction} undo for ${itemName}`,
    },
  ];
};

/**
 * Helper function to create undo callback
 */
export const createUndoCallback = (
  handleUndo: (uid: string) => void,
  itemUid: string
) => {
  return () => handleUndo(itemUid);
};

/**
 * Helper function to create dismiss callback
 */
export const createDismissCallback = (
  handleDismiss: (uid: string) => void,
  itemUid: string
) => {
  return () => handleDismiss(itemUid);
};

export const createUndoBanner = (
  removed: { item: CartModel['items'][number]; index: number; error?: string },
  item: CartModel['items'][number],
  isUndoBeingRemoved: boolean,
  dictionary: any,
  handleUndo: (uid: string) => void,
  handleDismiss: (uid: string) => void,
  slots?: CartSummaryTableContainerProps['slots']
): VNode => {
  const additionalActions = createUndoBannerActions(
    isUndoBeingRemoved,
    dictionary,
    handleUndo,
    handleDismiss,
    removed.item.uid,
    removed.item.name
  );

  const onUndoCallback = createUndoCallback(handleUndo, removed.item.uid);
  const onDismissCallback = createDismissCallback(handleDismiss, removed.item.uid);

  return (
    <Slot
      name="UndoBanner"
      slot={slots?.UndoBanner}
      context={{
        item: removed.item,
        loading: isUndoBeingRemoved,
        error: removed.error,
        onUndo: onUndoCallback,
        onDismiss: onDismissCallback,
      }}
      key={`undo-banner-${removed.item.uid}`}
    >
      <InLineAlert
        key={`undo-banner-${removed.item.uid}`}
        type={removed.error ? 'error' : 'info'}
        heading={
          isUndoBeingRemoved
            ? dictionary.itemBeingRemoved?.replace('{product}', item.name)
            : dictionary.itemRemoved?.replace('{product}', removed.item.name)
        }
        description={removed.error || dictionary.itemRemovedDescription}
        variant="primary"
        actionButtonPosition="bottom"
        additionalActions={additionalActions}
      />
    </Slot>
  );
};
